import React, { useState, useEffect, useCallback } from "react";
import CommunityHeader from "./header.js";
import Footer from "../landingPage/Footer.js";
import toast from "react-hot-toast";
import StoryModal from "./storyModel.js";
import BlogList from "./blogList.js";
import ReviewList from "./reviewList.js";
import PresentationList from "./presentationList.js";
import communityService from "../services/communityService.js";

const CommunityPage = () => {
  const [activeTab, setActiveTab] = useState("blogs");
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [storyForm, setStoryForm] = useState({
    title: "",
    name: "",
    image: null,
    content: "",
    previewImage: null,
  });
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    feedback: "",
    hoverRating: 0,
  });
  const [blogs, setBlogs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  // Improved tab navigation component
  const TabNavigation = ({ activeTab, setActiveTab }) => {
    return (
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {["blogs", "reviews", "presentations"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>
    );
  };

  const fetchData = useCallback(() => {
    let isMounted = true;

    const fetchAsync = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (activeTab === "blogs") {
          const result = await communityService.fetchStories(pagination.page, pagination.limit);
          if (isMounted) {
            if (result.success) {
              setBlogs(result.stories);
              setPagination((prev) => ({
                ...prev,
                total: result.total,
              }));
            } else {
              setError(result.error);
              toast.error(result.error);
            }
          }
        } else if (activeTab === "reviews") {
          const result = await communityService.fetchReviews(pagination.page, pagination.limit);
          if (isMounted) {
            if (result.success) {
              setReviews(result.reviews);
              setPagination((prev) => ({ 
                ...prev, 
                total: result.total 
              }));
            } else {
              setError(result.error);
              toast.error(result.error);
            }
          }
        } else if (activeTab === "presentations") {
          const result = await communityService.fetchPresentations(pagination.page, pagination.limit);
          if (isMounted) {
            if (result.success) {
              setPresentations(result.presentations);
              setPagination((prev) => ({ 
                ...prev, 
                total: result.total 
              }));
            } else {
              setError(result.error);
              toast.error(result.error);
            }
          }
        }
        
        if (isMounted) setLoading(false);
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          setLoading(false);
          toast.error("Failed to load data");
        }
      }
    };

    fetchAsync();

    return () => {
      isMounted = false;
    };
  }, [activeTab, pagination.page, pagination.limit]);

  useEffect(() => {
    const isAuth = communityService.isAuthenticated();
    setIsAuthenticated(isAuth);

    // Fetch current user data if authenticated
    if (isAuth) {
      fetchCurrentUser();
    }

    const cancel = fetchData();
    return cancel; // ensure the cleanup function is returned from useEffect
  }, [fetchData]);

  const fetchCurrentUser = async () => {
    const result = await communityService.getCurrentUser();
    if (result.success) {
      setCurrentUser(result.user);
    } else {
      console.error("Error fetching current user:", result.error);
    }
  };

  const handleUpdateStory = async (storyId, updatedData) => {
    const result = await communityService.updateStory(storyId, updatedData);
    
    if (result.success) {
      toast.success(result.message);
      
      // Update the local blogs state
      setBlogs(prevBlogs => 
        prevBlogs.map(blog => 
          blog.id === storyId 
            ? { ...blog, ...updatedData, updatedAt: new Date().toISOString() }
            : blog
        )
      );
      
      return true;
    } else {
      toast.error(result.error);
      return false;
    }
  };

  const handleStorySubmit = async (e) => {
    e.preventDefault();

    if (!storyForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    if (!storyForm.content.trim()) {
      toast.error("Please share your story");
      return;
    }

    const result = await communityService.createStory({
      title: storyForm.title,
      name: storyForm.name || "Anonymous",
      content: storyForm.content,
      image: storyForm.image
    });

    if (result.success) {
      toast.success(result.message);
      await fetchData();

      setStoryForm({
        title: "",
        name: "",
        image: null,
        content: "",
        previewImage: null,
      });
      setShowStoryModal(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!reviewForm.rating) {
      toast.error("Please select a rating");
      return;
    }

    if (!reviewForm.feedback.trim()) {
      toast.error("Please share your feedback");
      return;
    }

    const result = await communityService.createReview({
      rating: reviewForm.rating,
      feedback: reviewForm.feedback,
    });

    if (result.success) {
      toast.success(result.message);
      setReviewForm({
        rating: 0,
        feedback: "",
        hoverRating: 0,
      });
      await fetchData();
    } else {
      toast.error(result.error);
    }
  };

  const handleAddComment = async (reviewId, comment) => {
    const result = await communityService.addComment(reviewId, comment);
    
    if (result.success) {
      toast.success(result.message);
      await fetchData();
    } else {
      toast.error(result.error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStoryForm({
          ...storyForm,
          image: file,
          previewImage: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRatingChange = (rating) => {
    setReviewForm({ ...reviewForm, rating });
  };

  const handleRatingHover = (hoverRating) => {
    setReviewForm({ ...reviewForm, hoverRating });
  };

  const handleRatingLeave = () => {
    setReviewForm({ ...reviewForm, hoverRating: 0 });
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );

  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          <p>Error loading content: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );

  return (
    <div className="bg-gray-50 min-h-screen">
      <CommunityHeader isAuthenticated={isAuthenticated} />
<div className="page-header text-white py-16 px-4 text-center">
        <h1 className="text-4xl font-bold mb-4">Community Hub</h1>
        <p className="text-xl max-w-2xl mx-auto">
          Share your experiences, read stories, and connect with other members
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      {/* Story Modal */}
      <StoryModal
        show={showStoryModal}
        onClose={() => setShowStoryModal(false)}
        onSubmit={handleStorySubmit}
        formData={storyForm}
        onInputChange={(e) =>
          setStoryForm({ ...storyForm, [e.target.name]: e.target.value })
        }
        onImageChange={handleImageChange}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === "blogs" && (
          <BlogList
            blogs={blogs}
            onOpenModal={() => setShowStoryModal(true)}
            onUpdateStory={handleUpdateStory}
            currentUser={currentUser}
            isAuthenticated={isAuthenticated}
            pagination={pagination}
            onPageChange={(page) =>
              setPagination((prev) => ({ ...prev, page }))
            }
          />
        )}
        
        {activeTab === "reviews" && (
          <div className="space-y-8">
            {isAuthenticated && (
              <form
                onSubmit={handleReviewSubmit}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Share Your Experience
                </h2>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="rating"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Rating
                    </label>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onMouseEnter={() => handleRatingHover(star)}
                          onMouseLeave={handleRatingLeave}
                          onClick={() => handleRatingChange(star)}
                          className="focus:outline-none"
                        >
                          <svg
                            className={`h-8 w-8 ${
                              star <=
                              (reviewForm.hoverRating || reviewForm.rating)
                                ? "text-yellow-400"
                                : "text-gray-300"
                            }`}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="feedback"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Your Feedback
                    </label>
                    <textarea
                      id="feedback"
                      name="feedback"
                      value={reviewForm.feedback}
                      rows="4"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2"
                      placeholder="Share your experience with Ajo App..."
                      onChange={(e) =>
                        setReviewForm({
                          ...reviewForm,
                          feedback: e.target.value,
                        })
                      }
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Submit Review
                  </button>
                </div>
              </form>
            )}
            <ReviewList
              reviews={reviews}
              onAddComment={handleAddComment}
              pagination={pagination}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, page }))
              }
            />
          </div>
        )}

        {activeTab === "presentations" && (
          <PresentationList presentations={presentations} />
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CommunityPage;
